import React from 'react';
import getOptimizedUrl from '../utils/imageUtils';

const CheckoutPriceBreakdownSidebar = ({
  isOpen,
  onClose,
  items = [],
  subtotal = 0,
  discount = 0,
  total = 0,
}) => {
  const [mounted, setMounted] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const formatAmount = (value) => Number(value || 0).toLocaleString('en-IN');

  // Prevent body scroll when sidebar is open
  React.useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, 300);
    }
  }, [isOpen, mounted]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setMounted(false);
      setClosing(false);
      onClose();
    }, 300);
  };

  if (!mounted) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[10000]" onClick={handleClose}></div>

      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:max-w-md bg-white z-[10001] shadow-2xl flex flex-col drawer-panel ${
          closing ? 'drawer-slide-out' : 'drawer-slide-in'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 sm:p-2">
            <h2 className="text-black text-base font-bdogrotesk font-medium">Price Breakdown</h2>
            <button onClick={handleClose} className="text-black text-base font-bdogrotesk font-medium">
              Close
            </button>
          </div>

          <div className="flex-1 overflow-y-auto no-scrollbar px-4 sm:px-4 py-4">
            <div
              className="w-full max-w-sm mx-auto uppercase"
              style={{
                fontFamily: "'Courier Prime', monospace",
                letterSpacing: '-0.028em',
                fontWeight: 'normal',
              }}
            >
              <div className="border-t-2 border-dashed border-black my-2"></div>

              {items.length > 0 ? (
                <div className="space-y-6">
                  {items.map((item, index) => {
                    const qty = Number(item.quantity || 0);
                    const unitMrp = Number(item.unitMrp ?? item.discountedPrice ?? 0);
                    const unitDiscountedPrice = Number(item.discountedPrice ?? item.unitMrp ?? 0);
                    const netAmount = unitDiscountedPrice * qty;

                    return (
                    <div key={`${item.productId || item.productName || 'item'}-${index}`}>
                      <div className="flex gap-3">
                        {item.image && (
                          <div className="w-32 h-40 bg-white overflow-hidden flex-shrink-0">
                            <img
                              src={getOptimizedUrl(item.image || '', 800)}
                              alt={item.productName || 'Product'}
                              className="w-full h-full object-cover bg-white"
                            />
                          </div>
                        )}

                        <div className="flex-1 flex flex-col justify-between">
                          <div>
                            <div className="text-base">{item.productName}</div>
                            {item.size ? <div className="text-base">{item.size}</div> : null}
                          </div>

                          <div className="text-base">
                            <div className="flex justify-between">
                              <span>ORIGINAL PRICE:</span>
                              <span className={unitMrp > unitDiscountedPrice ? 'line-through decoration-1' : ''}>
                                {formatAmount(unitMrp)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>DISCOUNTED PRICE:</span>
                              <span>{formatAmount(unitDiscountedPrice)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>QTY:</span>
                              <span>{qty}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>NET AMOUNT:</span>
                              <span>{formatAmount(netAmount)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border-t-2 border-dashed border-black mt-6"></div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-base py-8">No items</div>
              )}

              <div className="space-y-2 text-base mt-4">
                <div className="flex justify-between">
                  <span>SUB TOTAL:</span>
                  <span>{formatAmount(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>DISCOUNT:</span>
                  <span>-{formatAmount(discount)}</span>
                </div>
              </div>

              <div className="border-t-2 border-dashed border-black my-4"></div>

              <div className="flex justify-between text-base">
                <span>DISCOUNTED PRICE:</span>
                <span>{formatAmount(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPriceBreakdownSidebar;
